import { useState } from "react";
import {
  Grid,
  Typography,
  Box,
  TableBody,
  TableRow,
  TableHead,
  Table,
  TableContainer,
  SvgIcon,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  FormControl,
  useMediaQuery,
  useTheme,
  Chip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { StyledPaper } from "../../../customComponents/styled/styledPaper";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ReactComponent as EditIcon } from "../../../../assets/icons/edit.svg";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../customComponents/styled/styledTable";
import EditTraitCategoryForm from "./EditTraitCategoryForm";
import {
  useDeleteBehaviorMutation,
  useDeleteTraitMutation,
  useGetTraitQuery,
  useCopyTraitMutation,
  useCopySubsectionTraitsMutation,
} from "./traitsApiSlice";
import { useGetSubSectionsPaginatedQuery } from "../sections/sectionsApiSlice";
import EditBehaviorForm from "./EditBehaviorForm";
import CreateTraitForm from "./CreateTraitForm";
import CreateBehaviorForm from "./CreateBehaviorForm";
import CustomWarningDialog from "../../../customComponents/CustomWarningDialog";
import toast from "../../../../helpers/toast";
import { useEffect } from "react";
import { StyledTextField } from "../../../customComponents/styled/styledInputs";

function Traits() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: traits, isLoading: getTraitsLoading } = useGetTraitQuery("all");
  const [deleteTrait] = useDeleteTraitMutation();
  const [deleteBehavior] = useDeleteBehaviorMutation();
  const [copyTrait, { isLoading: copyTraitLoading }] = useCopyTraitMutation();
  const [focusedCategory, setFocusedCategory] = useState(null); //for editing
  const [selectedTrait, setSelectedTrait] = useState(null);
  const [selectedBehavior, setSelectedBehavior] = useState(null);
  const [openWarning, setOpenWarning] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openBehaviorsDialog, setOpenBehaviorsDialog] = useState(false);
  const [openCopyDialog, setOpenCopyDialog] = useState(false);
  const [traitToCopy, setTraitToCopy] = useState(null);
  const [copySubsectionSearch, setCopySubsectionSearch] = useState("");
  const [copySubsectionId, setCopySubsectionId] = useState(null);
  const [openCopySubsectionDialog, setOpenCopySubsectionDialog] = useState(false);
  const [subsectionToCopy, setSubsectionToCopy] = useState(null); // { id, name }
  const [copySubsectionTargetSearch, setCopySubsectionTargetSearch] = useState("");
  const [copySubsectionTargetId, setCopySubsectionTargetId] = useState(null);
  const [copySubsectionTraits, { isLoading: copySubsectionLoading }] = useCopySubsectionTraitsMutation();

  const dialogOpen = openCopyDialog || openCopySubsectionDialog;
  const { data: subsectionsData } = useGetSubSectionsPaginatedQuery(
    { page: 1, per_page: 20, q: openCopySubsectionDialog ? copySubsectionTargetSearch : copySubsectionSearch },
    { skip: !dialogOpen }
  );
  const subsections = subsectionsData?.subsections ?? [];

  useEffect(() => {
    const updatedTraits = traits?.find(
      (trait) => trait.id === selectedTrait?.id
    );
    setSelectedTrait(updatedTraits);
  }, [traits]);

  const handleDelete = async ({ fn, item }) => {
    try {
      const response = await fn(item).unwrap();
      if (response.message) {
        toast.success(response.message);
      } else {
        toast.success("Error deleting item");
      }
    } catch (error) {
      toast.error("Somethng went wrong");
    }
  };

  const handleCopyTrait = async () => {
    if (!traitToCopy || !copySubsectionId) return;
    try {
      await copyTrait({ id: traitToCopy.id, subsection_id: copySubsectionId }).unwrap();
      toast.success(`"${traitToCopy.name}" copied successfully`);
      setOpenCopyDialog(false);
      setTraitToCopy(null);
      setCopySubsectionId(null);
      setCopySubsectionSearch("");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to copy trait");
    }
  };

  const handleCopySubsection = async () => {
    if (!subsectionToCopy || !copySubsectionTargetId) return;
    try {
      const response = await copySubsectionTraits({
        source_subsection_id: subsectionToCopy.id,
        target_subsection_id: copySubsectionTargetId,
      }).unwrap();
      toast.success(response.message || "Subsection copied successfully");
      setOpenCopySubsectionDialog(false);
      setSubsectionToCopy(null);
      setCopySubsectionTargetId(null);
      setCopySubsectionTargetSearch("");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to copy subsection traits");
    }
  };

  return (
    <>
      <CustomWarningDialog
        open={openWarning}
        setOpen={setOpenWarning}
        action={handleDelete}
        message="Are you sure you want to delete this?"
        item={itemToDelete}
      />
      <Dialog
        open={openCopyDialog}
        onClose={() => {
          setOpenCopyDialog(false);
          setTraitToCopy(null);
          setCopySubsectionId(null);
          setCopySubsectionSearch("");
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Copy "{traitToCopy?.name}"</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select the subsection to copy this trait and all its behaviours to.
          </Typography>
          <FormControl fullWidth>
            <Autocomplete
              options={subsections}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={subsections.find((s) => s.id === copySubsectionId) || null}
              onChange={(_, value) => setCopySubsectionId(value?.id || null)}
              onInputChange={(_, value, reason) => {
                if (reason === "input") setCopySubsectionSearch(value);
              }}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  placeholder="Search subsection..."
                  variant="outlined"
                />
              )}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCopyDialog(false);
              setTraitToCopy(null);
              setCopySubsectionId(null);
              setCopySubsectionSearch("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCopyTrait}
            disabled={!copySubsectionId || copyTraitLoading}
            variant="contained"
          >
            {copyTraitLoading ? "Copying..." : "Copy"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openCopySubsectionDialog}
        onClose={() => {
          setOpenCopySubsectionDialog(false);
          setSubsectionToCopy(null);
          setCopySubsectionTargetId(null);
          setCopySubsectionTargetSearch("");
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Copy all traits from "{subsectionToCopy?.name}"</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select the target subsection. All traits and their behaviours from "{subsectionToCopy?.name}" will be copied into it.
          </Typography>
          <FormControl fullWidth>
            <Autocomplete
              options={subsections.filter((s) => s.id !== subsectionToCopy?.id)}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={subsections.find((s) => s.id === copySubsectionTargetId) || null}
              onChange={(_, value) => setCopySubsectionTargetId(value?.id || null)}
              onInputChange={(_, value, reason) => {
                if (reason === "input") setCopySubsectionTargetSearch(value);
              }}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  placeholder="Search subsection..."
                  variant="outlined"
                />
              )}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCopySubsectionDialog(false);
              setSubsectionToCopy(null);
              setCopySubsectionTargetId(null);
              setCopySubsectionTargetSearch("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCopySubsection}
            disabled={!copySubsectionTargetId || copySubsectionLoading}
            variant="contained"
          >
            {copySubsectionLoading ? "Copying..." : "Copy All"}
          </Button>
        </DialogActions>
      </Dialog>
      <Grid container columnSpacing={3}>
        <Grid item xs={12} md={4}>
          <StyledPaper>
            {focusedCategory ? (
              <EditTraitCategoryForm
                item={focusedCategory}
                setItem={setFocusedCategory}
              />
            ) : (
              <CreateTraitForm />
            )}
          </StyledPaper>

          <StyledPaper>
            {selectedBehavior ? (
              <EditBehaviorForm
                item={selectedBehavior}
                setItem={setSelectedBehavior}
                traits={traits}
                selectedTrait={selectedTrait}
              />
            ) : (
              <CreateBehaviorForm />
            )}
          </StyledPaper>
        </Grid>
        <Grid item xs={12} md={8}>
          <StyledPaper>
            {getTraitsLoading && <LinearProgress />}
            <Box className="paperHeader">
              <Typography variant="formHeading">Traits</Typography>
            </Box>
            {/* ── Subsection-scoped traits ── */}
            {Object.entries(
              (traits ?? [])
                .filter((t) => !t.classroom_id)
                .reduce((groups, trait) => {
                  const key = trait.subsection?.name ?? "Uncategorized";
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(trait);
                  return groups;
                }, {})
            ).map(([subsectionName, subsectionTraits]) => (
              <Box key={subsectionName} sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1 }}>
                  <Typography
                    variant="formSubHeading"
                    sx={{ fontWeight: 600 }}
                  >
                    {subsectionName}
                  </Typography>
                  <ContentCopyIcon
                    className="tableCellLink"
                    sx={{ fontSize: 16, color: "text.secondary" }}
                    titleAccess="Copy all traits in this subsection"
                    onClick={() => {
                      const sub = subsectionTraits[0]?.subsection;
                      setSubsectionToCopy(sub ? { id: sub.id, name: sub.name } : { id: null, name: subsectionName });
                      setCopySubsectionTargetId(null);
                      setCopySubsectionTargetSearch("");
                      setOpenCopySubsectionDialog(true);
                    }}
                  />
                </Box>
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table sx={{ width: "100%", boxShadow: "none" }}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Trait</StyledTableCell>
                        <StyledTableCell align="left">Color</StyledTableCell>
                        <StyledTableCell align="left">edit</StyledTableCell>
                        <StyledTableCell align="left">copy</StyledTableCell>
                        <StyledTableCell align="left">delete</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subsectionTraits.map((row) => (
                        <StyledTableRow key={row.id}>
                          <StyledTableCell component="th" scope="row">
                            <Box
                              className="tableCellLink"
                              onClick={() => {
                                setSelectedTrait(row);
                                setOpenBehaviorsDialog(true);
                              }}
                            >
                              {row.name}
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                bgcolor: row.color || "#000000",
                                border: "1px solid rgba(0,0,0,0.15)",
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <SvgIcon
                              className="tableCellLink"
                              component={EditIcon}
                              inheritViewBox
                              onClick={() => setFocusedCategory(row)}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <ContentCopyIcon
                              className="tableCellLink"
                              sx={{ fontSize: 18 }}
                              onClick={() => {
                                setTraitToCopy(row);
                                setCopySubsectionId(null);
                                setCopySubsectionSearch("");
                                setOpenCopyDialog(true);
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <DeleteIcon
                              className="tableCellLink"
                              onClick={() => {
                                setItemToDelete({ item: row, fn: deleteTrait });
                                setOpenWarning(true);
                              }}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
            {/* ── Classroom-specific traits ── */}
            {(traits ?? []).filter((t) => Boolean(t.classroom_id)).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ px: 2, py: 1, borderTop: "2px dashed", borderColor: "divider" }}>
                  <Typography variant="formSubHeading" sx={{ fontWeight: 600, color: "primary.main" }}>
                    Classroom-Specific Traits
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    These traits override subsection traits for their assigned classroom
                  </Typography>
                </Box>
                {Object.entries(
                  (traits ?? [])
                    .filter((t) => Boolean(t.classroom_id))
                    .reduce((groups, trait) => {
                      const key = trait.classroom?.name ?? "Unknown Classroom";
                      if (!groups[key]) groups[key] = [];
                      groups[key].push(trait);
                      return groups;
                    }, {})
                ).map(([classroomName, classroomTraits]) => (
                  <Box key={classroomName} sx={{ mb: 3 }}>
                    <Box sx={{ px: 2, py: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Class: {classroomName}
                      </Typography>
                    </Box>
                    <TableContainer sx={{ overflowX: "auto" }}>
                      <Table sx={{ width: "100%", boxShadow: "none" }}>
                        <TableHead>
                          <TableRow>
                            <StyledTableCell>Trait</StyledTableCell>
                            <StyledTableCell align="left">Color</StyledTableCell>
                            <StyledTableCell align="left">edit</StyledTableCell>
                            <StyledTableCell align="left">delete</StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {classroomTraits.map((row) => (
                            <StyledTableRow key={row.id}>
                              <StyledTableCell component="th" scope="row">
                                <Box
                                  className="tableCellLink"
                                  onClick={() => {
                                    setSelectedTrait(row);
                                    setOpenBehaviorsDialog(true);
                                  }}
                                >
                                  {row.name}
                                </Box>
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    bgcolor: row.color || "#000000",
                                    border: "1px solid rgba(0,0,0,0.15)",
                                  }}
                                />
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                <SvgIcon
                                  className="tableCellLink"
                                  component={EditIcon}
                                  inheritViewBox
                                  onClick={() => setFocusedCategory(row)}
                                />
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                <DeleteIcon
                                  className="tableCellLink"
                                  onClick={() => {
                                    setItemToDelete({ item: row, fn: deleteTrait });
                                    setOpenWarning(true);
                                  }}
                                />
                              </StyledTableCell>
                            </StyledTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </Box>
            )}
          </StyledPaper>

          {/* Behaviors Dialog */}
          <Dialog
            open={openBehaviorsDialog}
            onClose={() => setOpenBehaviorsDialog(false)}
            fullScreen={isMobile}
            fullWidth
            maxWidth="sm"
            scroll="paper"
          >
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
              <Box>
                <Typography variant="h6" component="span">
                  {selectedTrait?.name}
                </Typography>
                {selectedTrait?.behaviors?.length > 0 && (
                  <Chip
                    label={selectedTrait.behaviors.length}
                    size="small"
                    sx={{ ml: 1, verticalAlign: "middle" }}
                  />
                )}
              </Box>
              <IconButton size="small" onClick={() => setOpenBehaviorsDialog(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              {selectedTrait?.behaviors?.length > 0 ? (
                <TableContainer>
                  <Table sx={{ width: "100%", boxShadow: "none" }}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Behavior</StyledTableCell>
                        <StyledTableCell align="right">Edit</StyledTableCell>
                        <StyledTableCell align="right">Delete</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedTrait.behaviors.map((row) => (
                        <StyledTableRow key={row.id}>
                          <StyledTableCell component="th" scope="row">
                            {row.name}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <SvgIcon
                              sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                              component={EditIcon}
                              inheritViewBox
                              onClick={() => {
                                setSelectedBehavior(row);
                                setOpenBehaviorsDialog(false);
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <DeleteIcon
                              sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                              onClick={() => {
                                setItemToDelete({ item: row, fn: deleteBehavior });
                                setOpenWarning(true);
                              }}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No behaviors found for this trait.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenBehaviorsDialog(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Grid>
    </>
  );
}

export default Traits;
